import { useParams } from "@tanstack/react-router";
import { Grid3X3, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRecordsViewMode } from "@/components/records/records-view/records-view-mode";
import { useProject } from "@/hooks/use-project";

interface RecordsViewToggleProps {
  /** Optional sizing from the host. */
  className?: string;
}

/**
 * Grid/list toggle for the photos page, at the trailing end of its title row
 * at every width ([[records-view-toggle.spec]], [[page-title-row.spec]]).
 *
 * Hidden until the project has at least one record: an empty state offers
 * nothing to present either way. The project comes from the query the layout
 * already loaded, so reading it here costs no extra fetch.
 */
export function RecordsViewToggle({ className }: RecordsViewToggleProps) {
  const { projectId } = useParams({ from: "/projects/$projectId" });
  const { data: project } = useProject(projectId);
  const [viewMode, setViewMode] = useRecordsViewMode();

  if ((project?.records_count ?? 0) === 0) return null;

  return (
    <div
      role="group"
      aria-label="Photos view mode"
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-md border bg-background p-0.5",
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Grid view"
        aria-pressed={viewMode === "grid"}
        onClick={() => setViewMode("grid")}
        className={cn(viewMode === "grid" && "bg-accent text-accent-foreground")}
      >
        <Grid3X3 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="List view"
        aria-pressed={viewMode === "list"}
        onClick={() => setViewMode("list")}
        className={cn(viewMode === "list" && "bg-accent text-accent-foreground")}
      >
        <List className="h-4 w-4" />
      </Button>
    </div>
  );
}
